/**
 * Accounting Unit Tests - Years 11-13
 * Vanuatu National Syllabus
 * Last Updated: February 3, 2026
 */

// ==================== YEAR 11 ACCOUNTING UNIT TESTS ====================
export const year11AccountingUnitTests = {
  strand1: {
    title: "Communicating Financial Information",
    tests: [
      {
        id: "1.1",
        title: "Conceptual Basis of Accounting - Test",
        duration: "45 minutes",
        totalMarks: 50,
        questions: [
          {
            id: 1,
            type: "definition",
            marks: 2,
            question: "Define accounting.",
            answer: "Accounting is the process of identifying, measuring, recording, and communicating financial information about an organization to users who need it for decision-making."
          },
          {
            id: 2,
            type: "short-answer",
            marks: 2,
            question: "State the basic accounting equation.",
            answer: "Assets = Liabilities + Owner's Equity"
          },
          {
            id: 3,
            type: "matching",
            marks: 5,
            question: "Match each financial element with its correct definition.",
            items: [
              { term: "Assets", definition: "Resources owned by the business with future economic value" },
              { term: "Liabilities", definition: "Obligations or debts owed to others" },
              { term: "Owner's Equity", definition: "The owner's claim on assets after all liabilities are paid" },
              { term: "Revenue", definition: "Income earned from business operations" },
              { term: "Expenses", definition: "Costs incurred to generate revenue" }
            ],
            answer: "Assets-Resources owned; Liabilities-Obligations owed; Owner's Equity-Owner's claim; Revenue-Income earned; Expenses-Costs incurred"
          },
          {
            id: 4,
            type: "multiple-choice",
            marks: 2,
            question: "Which of the following is a current asset?",
            options: ["Land", "Accounts Receivable", "Mortgage", "Share Capital"],
            answer: "Accounts Receivable"
          },
          {
            id: 5,
            type: "classification",
            marks: 6,
            question: "Classify each as Asset (A), Liability (L), Owner's Equity (OE), Revenue (R), or Expense (E): Cash, Bank Loan, Sales Revenue, Rent Expense, Capital, Inventory",
            answer: "Cash - A, Bank Loan - L, Sales Revenue - R, Rent Expense - E, Capital - OE, Inventory - A"
          },
          {
            id: 6,
            type: "short-answer",
            marks: 6,
            question: "List THREE advantages and THREE disadvantages of a sole proprietorship.",
            answer: "Advantages: (1) Easy to set up, (2) Full control over decisions, (3) All profits belong to owner. Disadvantages: (1) Unlimited liability, (2) Limited capital, (3) Heavy workload for owner."
          },
          {
            id: 7,
            type: "explanation",
            marks: 4,
            question: "Explain the business entity concept and why it is important.",
            answer: "The business entity concept states that the business is separate from its owner(s). This is important because it allows for accurate financial reporting, helps track business performance independently, and ensures personal transactions don't mix with business transactions."
          },
          {
            id: 8,
            type: "calculation",
            marks: 8,
            question: "Assets = $50,000, Liabilities = $20,000. Calculate Owner's Equity. If the business made a profit of $5,000, what would be the new Owner's Equity?",
            answer: "Owner's Equity = Assets - Liabilities = $50,000 - $20,000 = $30,000. New Owner's Equity = $30,000 + $5,000 = $35,000"
          },
          {
            id: 9,
            type: "application",
            marks: 10,
            question: "Show how each transaction affects the accounting equation: (a) Owner invests $10,000 cash (b) Purchased equipment for $3,000 cash (c) Purchased inventory on credit for $2,000 (d) Received $500 cash from customer",
            answer: "(a) Assets +$10,000 (Cash), OE +$10,000 (Capital)\n(b) Assets +$3,000 (Equipment), Assets -$3,000 (Cash) - No net change\n(c) Assets +$2,000 (Inventory), Liabilities +$2,000 (A/P)\n(d) Assets +$500 (Cash), OE +$500 (Revenue)"
          },
          {
            id: 10,
            type: "essay",
            marks: 5,
            question: "Describe THREE qualitative characteristics of accounting information and explain why each is important.",
            answer: "1. Relevance - Information must be useful for making decisions. 2. Reliability - Information must be accurate and verifiable. 3. Comparability - Information should be comparable across periods and with other businesses."
          }
        ]
      },
      {
        id: "1.2",
        title: "Internal Control - Test",
        duration: "40 minutes",
        totalMarks: 40,
        questions: [
          {
            id: 1,
            type: "listing",
            marks: 6,
            question: "List the SIX principles of internal control.",
            answer: "1. Establishment of responsibility\n2. Segregation of duties\n3. Documentation procedures\n4. Physical controls\n5. Independent internal verification\n6. Human resource controls"
          },
          {
            id: 2,
            type: "explanation",
            marks: 4,
            question: "Explain why segregation of duties is an important internal control principle.",
            answer: "Segregation of duties reduces the risk of errors and fraud by ensuring that no single person handles all aspects of a transaction. When duties are divided, the work of one person serves as a check on another, making it harder to commit fraud without detection."
          },
          {
            id: 3,
            type: "application",
            marks: 8,
            question: "Identify which principle each relates to: (a) Using a safe for cash (b) Requiring two signatures on cheques (c) Conducting surprise cash counts (d) Using pre-numbered receipts",
            answer: "(a) Physical controls\n(b) Segregation of duties / Authorization\n(c) Independent internal verification\n(d) Documentation procedures"
          },
          {
            id: 4,
            type: "case-study",
            marks: 10,
            question: "ABC Shop's cashier receives cash, records it, deposits money at the bank, and reconciles the bank statement. What weaknesses exist? Suggest improvements.",
            answer: "Weaknesses: One person handles all cash functions (no segregation). This person could steal cash and cover it up.\n\nImprovements: 1. Separate roles - different people receive, record, and reconcile. 2. Daily deposits with independent verification. 3. Someone else reconciles bank statement. 4. Surprise cash counts by management."
          },
          {
            id: 5,
            type: "calculation",
            marks: 12,
            question: "Petty cash fund of $500 established. Payments: Postage $45, Office supplies $78, Taxi $35, Cleaning $42. Calculate: (a) Total payments (b) Cash remaining (c) Reimbursement needed",
            answer: "(a) Total payments = $45 + $78 + $35 + $42 = $200\n(b) Cash remaining = $500 - $200 = $300\n(c) Reimbursement needed = $200 (to restore fund to $500)"
          }
        ]
      }
    ]
  },
  strand2: {
    title: "Recording Financial Information",
    tests: [
      {
        id: "2.1",
        title: "Double-Entry Bookkeeping - Test",
        duration: "60 minutes",
        totalMarks: 60,
        questions: [
          {
            id: 1,
            type: "listing",
            marks: 8,
            question: "List the EIGHT stages of the accounting cycle in order.",
            answer: "1. Identify transactions\n2. Record in journals\n3. Post to ledger\n4. Prepare trial balance\n5. Make adjusting entries\n6. Prepare financial statements\n7. Close accounts\n8. Prepare post-closing trial balance"
          },
          {
            id: 2,
            type: "matching",
            marks: 6,
            question: "Match each source document with its purpose: Invoice, Receipt, Cheque, Credit Note",
            answer: "Invoice - Records sales/purchases; Receipt - Confirms cash received; Cheque - Authorizes payment; Credit Note - Records returns"
          },
          {
            id: 3,
            type: "rules",
            marks: 6,
            question: "State the rules for debiting and crediting: Assets, Liabilities, Owner's Equity",
            answer: "Assets: Debit to increase, Credit to decrease\nLiabilities: Credit to increase, Debit to decrease\nOwner's Equity: Credit to increase, Debit to decrease"
          },
          {
            id: 4,
            type: "journal-entry",
            marks: 8,
            question: "Record journal entries: (a) Owner invested $15,000 cash (b) Purchased equipment for $4,000 cash (c) Sold goods on credit for $2,500",
            answer: "(a) Dr Cash $15,000, Cr Capital $15,000\n(b) Dr Equipment $4,000, Cr Cash $4,000\n(c) Dr Accounts Receivable $2,500, Cr Sales $2,500"
          },
          {
            id: 5,
            type: "ledger",
            marks: 12,
            question: "Post the following to T-accounts and balance: Opening Cash $5,000, Received from customer $1,200, Paid rent $800, Paid wages $600",
            answer: "Cash Account:\nDr: Opening $5,000, Customer $1,200 = $6,200\nCr: Rent $800, Wages $600 = $1,400\nBalance: $4,800 Dr"
          },
          {
            id: 6,
            type: "trial-balance",
            marks: 10,
            question: "Prepare a trial balance from: Cash $3,000, Accounts Receivable $2,000, Equipment $5,000, Accounts Payable $1,500, Capital $7,000, Sales $4,000, Wages $2,500",
            answer: "Trial Balance:\nDebits: Cash $3,000, A/R $2,000, Equipment $5,000, Wages $2,500 = $12,500\nCredits: A/P $1,500, Capital $7,000, Sales $4,000 = $12,500"
          },
          {
            id: 7,
            type: "error-correction",
            marks: 10,
            question: "A $500 cash sale was recorded as Dr Sales, Cr Cash. Identify the error and prepare the correcting entry.",
            answer: "Error: Debits and credits are reversed.\nCorrection: Dr Cash $1,000, Cr Sales $1,000 (reverse incorrect entry of $500 each way, then record correctly)"
          }
        ]
      },
      {
        id: "2.2",
        title: "Bank Reconciliation - Test",
        duration: "45 minutes",
        totalMarks: 45,
        questions: [
          {
            id: 1,
            type: "explanation",
            marks: 4,
            question: "Explain why the cash book balance often differs from the bank statement balance.",
            answer: "Differences arise from: (1) Outstanding cheques not yet presented (2) Deposits in transit (3) Bank charges not recorded in cash book (4) Direct debits/credits (5) Errors in either record."
          },
          {
            id: 2,
            type: "classification",
            marks: 8,
            question: "Classify as Bank Adjustment (BA) or Cash Book Adjustment (CA): (a) Outstanding cheques (b) Bank charges (c) Deposits in transit (d) Direct credit from customer",
            answer: "(a) BA - deduct from bank balance\n(b) CA - deduct from cash book\n(c) BA - add to bank balance\n(d) CA - add to cash book"
          },
          {
            id: 3,
            type: "calculation",
            marks: 15,
            question: "Bank statement shows $5,200. Outstanding cheques total $800, deposits in transit $400. Calculate adjusted bank balance.",
            answer: "Bank balance: $5,200\nAdd: Deposits in transit: $400\n     = $5,600\nLess: Outstanding cheques: ($800)\nAdjusted bank balance: $4,800"
          },
          {
            id: 4,
            type: "reconciliation",
            marks: 18,
            question: "Prepare bank reconciliation:\nBank statement: $4,500\nCash book: $4,150\nOutstanding cheques: $600\nDeposits in transit: $200\nBank charges: $50",
            answer: "Bank Reconciliation:\n\nBank Statement Balance: $4,500\nAdd: Deposits in transit: $200\n     = $4,700\nLess: Outstanding cheques: ($600)\nAdjusted Bank Balance: $4,100\n\nCash Book Balance: $4,150\nLess: Bank charges: ($50)\nAdjusted Cash Book Balance: $4,100\n\n(Both balances agree at $4,100)"
          }
        ]
      }
    ]
  },
  strand3: {
    title: "Reporting Financial Information",
    tests: [
      {
        id: "3.1",
        title: "Balance Day Adjustments - Test",
        duration: "50 minutes",
        totalMarks: 50,
        questions: [
          {
            id: 1,
            type: "explanation",
            marks: 4,
            question: "Explain why balance day adjustments are necessary.",
            answer: "Balance day adjustments ensure revenues and expenses are recorded in the correct accounting period (matching principle). They update accounts for prepayments, accruals, and depreciation to give a true and fair view of financial position."
          },
          {
            id: 2,
            type: "journal-entry",
            marks: 8,
            question: "Prepare adjusting entry: $1,200 prepaid insurance, 4 months have been used.",
            answer: "Insurance Expense for period = $1,200 × 4/12 = $400\n\nDr Insurance Expense $400\n   Cr Prepaid Insurance $400"
          },
          {
            id: 3,
            type: "journal-entry",
            marks: 8,
            question: "Wages of $1,800 are owing at year end. Prepare the adjusting entry.",
            answer: "Dr Wages Expense $1,800\n   Cr Wages Payable $1,800"
          },
          {
            id: 4,
            type: "calculation",
            marks: 10,
            question: "Equipment costs $24,000, salvage value $4,000, useful life 5 years. Calculate annual depreciation using straight-line method.",
            answer: "Depreciation = (Cost - Salvage) ÷ Life\n= ($24,000 - $4,000) ÷ 5\n= $20,000 ÷ 5\n= $4,000 per year"
          },
          {
            id: 5,
            type: "journal-entry",
            marks: 8,
            question: "Record annual depreciation from Q4.",
            answer: "Dr Depreciation Expense $4,000\n   Cr Accumulated Depreciation $4,000"
          },
          {
            id: 6,
            type: "application",
            marks: 12,
            question: "Received $900 rent in advance for 3 months. At year end, 2 months have passed. Prepare entries for: (a) Initial receipt (b) Year-end adjustment",
            answer: "(a) Initial receipt:\nDr Cash $900\n   Cr Unearned Rent $900\n\n(b) Year-end adjustment (2/3 earned):\nDr Unearned Rent $600\n   Cr Rent Revenue $600"
          }
        ]
      },
      {
        id: "3.2",
        title: "Financial Statements - Test",
        duration: "60 minutes",
        totalMarks: 60,
        questions: [
          {
            id: 1,
            type: "calculation",
            marks: 6,
            question: "Calculate Gross Profit: Sales $75,000, COGS $45,000",
            answer: "Gross Profit = Sales - COGS\n= $75,000 - $45,000\n= $30,000"
          },
          {
            id: 2,
            type: "calculation",
            marks: 8,
            question: "Calculate COGS: Opening Inventory $8,000, Purchases $32,000, Closing Inventory $10,000",
            answer: "COGS = Opening + Purchases - Closing\n= $8,000 + $32,000 - $10,000\n= $30,000"
          },
          {
            id: 3,
            type: "classification",
            marks: 8,
            question: "Classify as Current (C) or Non-Current (NC) Asset: Cash, Land, Inventory, Equipment, Accounts Receivable, Motor Vehicle",
            answer: "Cash - C\nLand - NC\nInventory - C\nEquipment - NC\nAccounts Receivable - C\nMotor Vehicle - NC"
          },
          {
            id: 4,
            type: "statement-preparation",
            marks: 20,
            question: "Prepare Income Statement: Sales $100,000, COGS $60,000, Wages $15,000, Rent $8,000, Utilities $3,000, Depreciation $4,000",
            answer: "INCOME STATEMENT\n\nSales Revenue: $100,000\nLess: COGS: ($60,000)\nGROSS PROFIT: $40,000\n\nLess: Operating Expenses:\n  Wages: $15,000\n  Rent: $8,000\n  Utilities: $3,000\n  Depreciation: $4,000\n  Total Expenses: ($30,000)\n\nNET PROFIT: $10,000"
          },
          {
            id: 5,
            type: "statement-preparation",
            marks: 18,
            question: "Prepare Statement of Financial Position from: Cash $5,000, Inventory $12,000, Equipment $30,000, Acc. Dep. $6,000, A/P $8,000, Capital $28,000, Net Profit $5,000",
            answer: "STATEMENT OF FINANCIAL POSITION\n\nASSETS:\nCurrent Assets:\n  Cash: $5,000\n  Inventory: $12,000\n  Total Current: $17,000\n\nNon-Current Assets:\n  Equipment: $30,000\n  Less: Acc. Dep: ($6,000)\n  Total NC: $24,000\n\nTOTAL ASSETS: $41,000\n\nLIABILITIES:\n  Accounts Payable: $8,000\n\nOWNER'S EQUITY:\n  Capital: $28,000\n  Add: Net Profit: $5,000\n  Total Equity: $33,000\n\nTOTAL L + E: $41,000"
          }
        ]
      }
    ]
  },
  strand4: {
    title: "Evaluating Financial Information",
    tests: [
      {
        id: "4.1",
        title: "Financial Ratio Analysis - Test",
        duration: "45 minutes",
        totalMarks: 45,
        questions: [
          {
            id: 1,
            type: "calculation",
            marks: 6,
            question: "Calculate Gross Profit Margin: Sales $80,000, Gross Profit $28,000",
            answer: "GP Margin = (GP ÷ Sales) × 100\n= ($28,000 ÷ $80,000) × 100\n= 35%"
          },
          {
            id: 2,
            type: "calculation",
            marks: 6,
            question: "Calculate Net Profit Margin: Sales $80,000, Net Profit $12,000",
            answer: "NP Margin = (NP ÷ Sales) × 100\n= ($12,000 ÷ $80,000) × 100\n= 15%"
          },
          {
            id: 3,
            type: "calculation",
            marks: 8,
            question: "Calculate Current Ratio: Current Assets $45,000, Current Liabilities $22,500",
            answer: "Current Ratio = CA ÷ CL\n= $45,000 ÷ $22,500\n= 2:1"
          },
          {
            id: 4,
            type: "calculation",
            marks: 8,
            question: "Calculate Quick Ratio: Current Assets $45,000, Inventory $15,000, Current Liabilities $22,500",
            answer: "Quick Ratio = (CA - Inventory) ÷ CL\n= ($45,000 - $15,000) ÷ $22,500\n= $30,000 ÷ $22,500\n= 1.33:1"
          },
          {
            id: 5,
            type: "interpretation",
            marks: 8,
            question: "Current ratio is 0.8:1. What does this indicate and what action should be taken?",
            answer: "A current ratio below 1:1 indicates the business cannot meet its short-term obligations from current assets. This is a liquidity problem.\n\nActions: (1) Reduce current liabilities (2) Increase current assets (3) Convert non-current assets to cash (4) Seek additional capital or loans"
          },
          {
            id: 6,
            type: "analysis",
            marks: 9,
            question: "Return on Equity: Net Profit $25,000, Owner's Equity $100,000. Calculate ROE and comment.",
            answer: "ROE = (Net Profit ÷ Owner's Equity) × 100\n= ($25,000 ÷ $100,000) × 100\n= 25%\n\nComment: A 25% return is excellent compared to typical bank interest rates (2-5%). The owner is earning a good return on their investment."
          }
        ]
      }
    ]
  }
};

// ==================== YEAR 12 ACCOUNTING UNIT TESTS ====================
export const year12AccountingUnitTests = {
  strand1: {
    title: "Advanced Internal Controls",
    tests: [
      {
        id: "1.1",
        title: "Internal Controls - Advanced Test",
        duration: "45 minutes",
        totalMarks: 45,
        questions: [
          {
            id: 1,
            type: "listing",
            marks: 8,
            question: "List FOUR internal control procedures for inventory.",
            answer: "1. Secure storage with limited access\n2. Perpetual inventory records\n3. Regular physical counts\n4. Segregation of duties (ordering, receiving, recording)\n5. Authorization for purchases\n6. Insurance coverage"
          },
          {
            id: 2,
            type: "explanation",
            marks: 6,
            question: "Explain the purpose of an aging schedule for accounts receivable.",
            answer: "An aging schedule classifies receivables by how long they've been outstanding (current, 30, 60, 90+ days). It helps: (1) Identify overdue accounts for follow-up (2) Estimate bad debts provision (3) Assess collection effectiveness (4) Make credit decisions."
          },
          {
            id: 3,
            type: "application",
            marks: 12,
            question: "Create an aging schedule: Customer A owes $1,500 (current $800, 30 days $500, 60 days $200), Customer B owes $2,000 (current $1,200, 90+ days $800)",
            answer: "AGING SCHEDULE:\n\n| Customer | Current | 30 Days | 60 Days | 90+ Days | Total |\n| A | $800 | $500 | $200 | $0 | $1,500 |\n| B | $1,200 | $0 | $0 | $800 | $2,000 |\n| TOTAL | $2,000 | $500 | $200 | $800 | $3,500 |"
          },
          {
            id: 4,
            type: "case-study",
            marks: 10,
            question: "A company has no inventory control system. Stock is stored in an open warehouse accessible to all staff. Identify THREE weaknesses and suggest improvements.",
            answer: "Weaknesses:\n1. No tracking system - unknown stock levels\n2. Open access - theft risk\n3. No accountability - no one responsible\n\nImprovements:\n1. Implement perpetual inventory system\n2. Secure storage with limited access\n3. Assign responsibility to specific staff\n4. Regular physical counts\n5. Segregate ordering/receiving/recording"
          },
          {
            id: 5,
            type: "explanation",
            marks: 9,
            question: "What information should an asset register contain? List at least FIVE items.",
            answer: "Asset Register should include:\n1. Asset description and identification number\n2. Date of purchase\n3. Cost of asset\n4. Depreciation method and rate\n5. Accumulated depreciation\n6. Current book value\n7. Location of asset\n8. Supplier details\n9. Expected useful life\n10. Disposal details (if applicable)"
          }
        ]
      }
    ]
  },
  strand2: {
    title: "Recording Financial Information",
    tests: [
      {
        id: "2.1",
        title: "Inventory Valuation - Test",
        duration: "60 minutes",
        totalMarks: 60,
        questions: [
          {
            id: 1,
            type: "comparison",
            marks: 8,
            question: "Compare periodic and perpetual inventory systems (list TWO advantages of each).",
            answer: "Periodic System:\n1. Simple and inexpensive\n2. Less record-keeping required\n\nPerpetual System:\n1. Real-time inventory levels known\n2. Better control over stock\n3. Quicker identification of theft/loss"
          },
          {
            id: 2,
            type: "calculation",
            marks: 15,
            question: "Using FIFO, calculate COGS and ending inventory:\nJan 1: 100 units @ $10\nJan 15: 150 units @ $12\nJan 25: 100 units @ $14\nSales: 200 units on Jan 31",
            answer: "FIFO - First costs go to COGS:\n\nCOGS:\n100 units @ $10 = $1,000\n100 units @ $12 = $1,200\nTotal COGS = $2,200\n\nEnding Inventory:\n50 units @ $12 = $600\n100 units @ $14 = $1,400\nTotal = $2,000"
          },
          {
            id: 3,
            type: "calculation",
            marks: 15,
            question: "Using Weighted Average, calculate COGS for the same data in Q2.",
            answer: "Weighted Average:\nTotal Cost = $1,000 + $1,800 + $1,400 = $4,200\nTotal Units = 100 + 150 + 100 = 350\n\nAverage Cost = $4,200 ÷ 350 = $12 per unit\n\nCOGS = 200 × $12 = $2,400\nEnding Inventory = 150 × $12 = $1,800"
          },
          {
            id: 4,
            type: "application",
            marks: 10,
            question: "Inventory cost is $25 per unit. Due to damage, it can only be sold for $20. At what value should it be recorded and why?",
            answer: "Value: $20 per unit\n\nReason: Apply Lower of Cost or Net Realizable Value (NRV) rule. Since NRV ($20) is lower than cost ($25), inventory must be written down to $20. This follows conservatism principle - don't overstate assets."
          },
          {
            id: 5,
            type: "journal-entry",
            marks: 12,
            question: "Record the write-down in Q4 for 500 units of inventory.",
            answer: "Write-down = 500 × ($25 - $20) = $2,500\n\nJournal Entry:\nDr Inventory Write-down Expense $2,500\n   Cr Inventory $2,500\n\n(OR alternative presentation)\nDr Cost of Goods Sold $2,500\n   Cr Inventory $2,500"
          }
        ]
      },
      {
        id: "2.2",
        title: "GST Accounting - Test",
        duration: "45 minutes",
        totalMarks: 45,
        questions: [
          {
            id: 1,
            type: "calculation",
            marks: 6,
            question: "Calculate GST (15% rate) on a purchase of $850 (GST exclusive).",
            answer: "GST = $850 × 15% = $127.50\nTotal including GST = $850 + $127.50 = $977.50"
          },
          {
            id: 2,
            type: "calculation",
            marks: 8,
            question: "A sale totals $575 including GST (15%). Calculate the GST amount and the sale price excluding GST.",
            answer: "GST = $575 × 15/115 = $75\nSale excluding GST = $575 - $75 = $500\n\n(OR: $575 ÷ 1.15 = $500, GST = $575 - $500 = $75)"
          },
          {
            id: 3,
            type: "journal-entry",
            marks: 10,
            question: "Record: Sold goods on credit for $460 including GST (15%).",
            answer: "GST = $460 × 15/115 = $60\nSales = $460 - $60 = $400\n\nJournal Entry:\nDr Accounts Receivable $460\n   Cr Sales $400\n   Cr GST Collected $60"
          },
          {
            id: 4,
            type: "journal-entry",
            marks: 10,
            question: "Record: Purchased inventory on credit for $690 including GST (15%).",
            answer: "GST = $690 × 15/115 = $90\nInventory = $690 - $90 = $600\n\nJournal Entry:\nDr Inventory $600\nDr GST Paid $90\n   Cr Accounts Payable $690"
          },
          {
            id: 5,
            type: "calculation-and-entry",
            marks: 11,
            question: "GST Collected is $12,000. GST Paid is $8,500. Calculate net GST and prepare the journal entry to settle with the government.",
            answer: "Net GST = $12,000 - $8,500 = $3,500 Payable\n\nJournal Entry:\nDr GST Collected $12,000\n   Cr GST Paid $8,500\n   Cr Cash $3,500\n\n(Paying $3,500 to government)"
          }
        ]
      }
    ]
  },
  strand3: {
    title: "Reporting Financial Information",
    tests: [
      {
        id: "3.1",
        title: "Not-for-Profit Accounting - Test",
        duration: "50 minutes",
        totalMarks: 50,
        questions: [
          {
            id: 1,
            type: "comparison",
            marks: 8,
            question: "List FOUR differences between profit and not-for-profit organizations.",
            answer: "1. Purpose: Profit - earn profit; NFP - social/service\n2. Owners: Profit - shareholders; NFP - members\n3. Profits: Profit - distributed; NFP - reinvested\n4. Equity: Profit - Owner's Equity; NFP - Accumulated Fund\n5. Terminology: Profit uses 'profit'; NFP uses 'surplus/deficit'"
          },
          {
            id: 2,
            type: "calculation",
            marks: 12,
            question: "A club has 150 members paying $40 each. 12 members haven't paid. 5 members paid next year in advance. Calculate subscription income.",
            answer: "Expected subscriptions = 150 × $40 = $6,000\nArrears (unpaid) = 12 × $40 = $480\nAdvance = 5 × $40 = $200\n\nSubscription Income = Expected - Arrears\n= $6,000 - $480 = $5,520\n\n(Advance is recorded as liability, not income yet)"
          },
          {
            id: 3,
            type: "statement-preparation",
            marks: 15,
            question: "Prepare Statement of Receipts and Payments: Opening cash $800, Subscriptions received $4,500, Donations $600, Rent paid $1,800, Wages paid $2,000, Equipment purchased $500",
            answer: "STATEMENT OF RECEIPTS AND PAYMENTS\n\nOPENING CASH: $800\n\nRECEIPTS:\nSubscriptions: $4,500\nDonations: $600\nTotal Receipts: $5,100\n\nTOTAL AVAILABLE: $5,900\n\nPAYMENTS:\nRent: $1,800\nWages: $2,000\nEquipment: $500\nTotal Payments: ($4,300)\n\nCLOSING CASH: $1,600"
          },
          {
            id: 4,
            type: "journal-entries",
            marks: 15,
            question: "Prepare entries for: (a) 10 members owe $50 each (arrears) (b) 3 members paid $50 each for next year (advance)",
            answer: "(a) Subscriptions in Arrears:\nDr Subscriptions Receivable $500\n   Cr Subscription Income $500\n\n(b) Subscriptions in Advance:\nDr Cash $150\n   Cr Subscriptions in Advance $150\n\n(Advance is a liability until earned)"
          }
        ]
      }
    ]
  },
  strand4: {
    title: "Evaluating Financial Information",
    tests: [
      {
        id: "4.1",
        title: "Advanced Ratio Analysis - Test",
        duration: "50 minutes",
        totalMarks: 50,
        questions: [
          {
            id: 1,
            type: "calculation",
            marks: 8,
            question: "Calculate Inventory Turnover: COGS $180,000, Opening Inventory $25,000, Closing Inventory $35,000",
            answer: "Average Inventory = ($25,000 + $35,000) ÷ 2 = $30,000\n\nInventory Turnover = COGS ÷ Average Inventory\n= $180,000 ÷ $30,000\n= 6 times per year"
          },
          {
            id: 2,
            type: "calculation",
            marks: 6,
            question: "Calculate Days Inventory from Q1.",
            answer: "Days Inventory = 365 ÷ Inventory Turnover\n= 365 ÷ 6\n= 61 days\n\n(On average, inventory is held for 61 days before being sold)"
          },
          {
            id: 3,
            type: "calculation",
            marks: 8,
            question: "Calculate Receivables Turnover and Collection Period: Credit Sales $240,000, Average Receivables $40,000",
            answer: "Receivables Turnover = Credit Sales ÷ Average Receivables\n= $240,000 ÷ $40,000\n= 6 times\n\nCollection Period = 365 ÷ 6 = 61 days"
          },
          {
            id: 4,
            type: "calculation",
            marks: 8,
            question: "Calculate Debt Ratio and Debt-to-Equity: Total Assets $500,000, Total Liabilities $175,000, Owner's Equity $325,000",
            answer: "Debt Ratio = Total Liabilities ÷ Total Assets\n= $175,000 ÷ $500,000\n= 35% (or 0.35)\n\nDebt-to-Equity = Total Liabilities ÷ Owner's Equity\n= $175,000 ÷ $325,000\n= 0.54:1"
          },
          {
            id: 5,
            type: "interpretation",
            marks: 10,
            question: "Collection period is 75 days but credit terms are 30 days. What does this indicate and what should management do?",
            answer: "Indication: Customers are taking much longer to pay than the credit terms allow. This indicates:\n- Poor credit control\n- Possible bad debt risk\n- Cash flow problems\n\nActions:\n1. Review credit approval procedures\n2. Send statements promptly\n3. Follow up overdue accounts aggressively\n4. Consider tighter credit terms\n5. Offer early payment discounts"
          },
          {
            id: 6,
            type: "analysis",
            marks: 10,
            question: "Compare Company A (Debt ratio 65%, Interest Cover 1.5) with Company B (Debt ratio 30%, Interest Cover 5). Which is more financially stable? Explain.",
            answer: "Company B is more financially stable.\n\nReasons:\n1. Lower debt ratio (30% vs 65%) - less reliance on debt\n2. Higher interest coverage (5x vs 1.5x) - better ability to pay interest\n3. Company A's interest cover of 1.5 is risky - small profit drop could mean inability to pay interest\n4. Company B has more buffer for economic downturns"
          }
        ]
      }
    ]
  }
};

// ==================== YEAR 13 ACCOUNTING UNIT TESTS ====================
export const year13AccountingUnitTests = {
  strand1: {
    title: "Company Accounting",
    tests: [
      {
        id: "1.1",
        title: "Share Capital and Dividends - Test",
        duration: "50 minutes",
        totalMarks: 50,
        questions: [
          {
            id: 1,
            type: "listing",
            marks: 6,
            question: "List FOUR characteristics of a company.",
            answer: "1. Separate legal entity\n2. Limited liability for shareholders\n3. Perpetual existence\n4. Transferable ownership\n5. Centralized management\n6. Complex regulations"
          },
          {
            id: 2,
            type: "comparison",
            marks: 6,
            question: "Compare ordinary and preference shares (TWO differences each).",
            answer: "Ordinary Shares:\n- Voting rights at meetings\n- Variable dividends based on profit\n\nPreference Shares:\n- Fixed dividend rate\n- Priority for dividends over ordinary shares\n- Usually no voting rights"
          },
          {
            id: 3,
            type: "calculation-and-entry",
            marks: 12,
            question: "A company issues 200,000 shares at $2.50 (par value $1.00). Calculate total cash received, share capital, and share premium. Prepare the journal entry.",
            answer: "Cash received = 200,000 × $2.50 = $500,000\nShare Capital = 200,000 × $1.00 = $200,000\nShare Premium = 200,000 × $1.50 = $300,000\n\nJournal Entry:\nDr Cash $500,000\n   Cr Share Capital $200,000\n   Cr Share Premium $300,000"
          },
          {
            id: 4,
            type: "calculation",
            marks: 10,
            question: "A company has 1,000,000 shares. It declares: Interim dividend 8 cents per share, Final dividend 12 cents per share. Calculate total dividends.",
            answer: "Interim dividend = 1,000,000 × $0.08 = $80,000\nFinal dividend = 1,000,000 × $0.12 = $120,000\nTotal dividends = $200,000"
          },
          {
            id: 5,
            type: "journal-entries",
            marks: 8,
            question: "Prepare entries for: (a) Interim dividend of $80,000 paid (b) Final dividend of $120,000 declared (c) Final dividend paid",
            answer: "(a) Interim paid:\nDr Retained Earnings $80,000\n   Cr Cash $80,000\n\n(b) Final declared:\nDr Retained Earnings $120,000\n   Cr Dividends Payable $120,000\n\n(c) Final paid:\nDr Dividends Payable $120,000\n   Cr Cash $120,000"
          },
          {
            id: 6,
            type: "bonus-issue",
            marks: 8,
            question: "A company has 400,000 shares (Share Capital $400,000). It makes a 1:4 bonus issue. Calculate bonus shares and prepare the entry.",
            answer: "Bonus shares = 400,000 ÷ 4 = 100,000 shares\nValue = 100,000 × $1 = $100,000\n\nJournal Entry:\nDr Retained Earnings $100,000\n   Cr Share Capital $100,000\n\nNew total shares = 500,000"
          }
        ]
      }
    ]
  },
  strand2: {
    title: "Advanced Recording",
    tests: [
      {
        id: "2.1",
        title: "Bad Debts and Provisions - Test",
        duration: "45 minutes",
        totalMarks: 45,
        questions: [
          {
            id: 1,
            type: "explanation",
            marks: 6,
            question: "Explain the difference between the direct write-off method and the allowance method.",
            answer: "Direct Write-off: Bad debts expensed only when specifically identified as uncollectible. Violates matching principle.\n\nAllowance Method: Estimates bad debts at year-end based on percentage of receivables. Better matching of expenses with related revenue. Creates a contra-asset account."
          },
          {
            id: 2,
            type: "calculation-and-entry",
            marks: 12,
            question: "Accounts Receivable $100,000. Existing allowance $3,000. Management wants 5% allowance. Calculate adjustment and prepare entry.",
            answer: "Required allowance = $100,000 × 5% = $5,000\nExisting allowance = $3,000\nIncrease needed = $5,000 - $3,000 = $2,000\n\nJournal Entry:\nDr Bad Debts Expense $2,000\n   Cr Allowance for Doubtful Debts $2,000"
          },
          {
            id: 3,
            type: "journal-entry",
            marks: 8,
            question: "Write off $1,500 owed by ABC Ltd using the allowance method.",
            answer: "Dr Allowance for Doubtful Debts $1,500\n   Cr Accounts Receivable $1,500\n\n(Note: Writing off against allowance - no expense impact)"
          },
          {
            id: 4,
            type: "journal-entries",
            marks: 10,
            question: "A $800 debt written off last year has been collected. Prepare the recovery entries.",
            answer: "Step 1 - Reinstate the debt:\nDr Accounts Receivable $800\n   Cr Bad Debts Recovered $800\n\nStep 2 - Record the collection:\nDr Cash $800\n   Cr Accounts Receivable $800"
          },
          {
            id: 5,
            type: "presentation",
            marks: 9,
            question: "Show how Accounts Receivable of $80,000 with Allowance of $4,000 appears on the Statement of Financial Position.",
            answer: "STATEMENT OF FINANCIAL POSITION (Extract)\n\nCurrent Assets:\nAccounts Receivable         $80,000\nLess: Allowance for\n      Doubtful Debts        (4,000)\nNet Receivables             $76,000"
          }
        ]
      },
      {
        id: "2.2",
        title: "Asset Disposal - Test",
        duration: "45 minutes",
        totalMarks: 45,
        questions: [
          {
            id: 1,
            type: "calculation",
            marks: 6,
            question: "Equipment cost $60,000, accumulated depreciation $42,000, sold for $22,000. Calculate gain or loss.",
            answer: "Book Value = Cost - Accumulated Depreciation\n= $60,000 - $42,000 = $18,000\n\nGain/Loss = Sale Price - Book Value\n= $22,000 - $18,000 = $4,000 GAIN"
          },
          {
            id: 2,
            type: "journal-entry",
            marks: 12,
            question: "Prepare the disposal entry for Q1.",
            answer: "Dr Cash $22,000\nDr Accumulated Depreciation $42,000\n   Cr Equipment $60,000\n   Cr Gain on Disposal $4,000"
          },
          {
            id: 3,
            type: "calculation-and-entry",
            marks: 12,
            question: "Motor vehicle cost $35,000, accumulated depreciation $28,000, sold for $5,000. Calculate gain/loss and prepare entry.",
            answer: "Book Value = $35,000 - $28,000 = $7,000\nLoss = $5,000 - $7,000 = ($2,000) LOSS\n\nJournal Entry:\nDr Cash $5,000\nDr Accumulated Depreciation $28,000\nDr Loss on Disposal $2,000\n   Cr Motor Vehicle $35,000"
          },
          {
            id: 4,
            type: "revaluation",
            marks: 8,
            question: "Building (book value $300,000) is revalued to $400,000. Prepare the journal entry.",
            answer: "Increase = $400,000 - $300,000 = $100,000\n\nJournal Entry:\nDr Building $100,000\n   Cr Revaluation Surplus $100,000\n\n(Note: Goes to equity, not profit)"
          },
          {
            id: 5,
            type: "trade-in",
            marks: 7,
            question: "Old computer (cost $8,000, acc. dep. $6,000) traded in for new computer ($12,000). Trade-in allowance $2,500. Prepare entry.",
            answer: "Book value = $8,000 - $6,000 = $2,000\nGain on trade = $2,500 - $2,000 = $500\nCash paid = $12,000 - $2,500 = $9,500\n\nJournal Entry:\nDr Computer (new) $12,000\nDr Accumulated Depreciation $6,000\n   Cr Computer (old) $8,000\n   Cr Cash $9,500\n   Cr Gain on Trade-in $500"
          }
        ]
      }
    ]
  },
  strand3: {
    title: "Company Financial Statements",
    tests: [
      {
        id: "3.1",
        title: "Company Statements - Test",
        duration: "60 minutes",
        totalMarks: 60,
        questions: [
          {
            id: 1,
            type: "calculation",
            marks: 8,
            question: "Calculate EPS: Net Profit After Tax $360,000, Ordinary Shares 1,200,000",
            answer: "EPS = Net Profit After Tax ÷ Ordinary Shares\n= $360,000 ÷ 1,200,000\n= $0.30 per share"
          },
          {
            id: 2,
            type: "calculation",
            marks: 8,
            question: "Calculate Total Shareholders' Equity: Share Capital $500,000, Share Premium $75,000, Revaluation Surplus $40,000, Retained Earnings $185,000",
            answer: "Total Shareholders' Equity:\nShare Capital: $500,000\nShare Premium: $75,000\nRevaluation Surplus: $40,000\nRetained Earnings: $185,000\n\nTotal = $800,000"
          },
          {
            id: 3,
            type: "statement-preparation",
            marks: 20,
            question: "Prepare Statement of Changes in Equity:\nOpening: Share Capital $400,000, Share Premium $60,000, Retained Earnings $150,000\nDuring year: Profit $90,000, Dividends $40,000, New shares 50,000 @ $1.50 (par $1)",
            answer: "STATEMENT OF CHANGES IN EQUITY\n\n                    Share    Share   Retained\n                   Capital  Premium  Earnings   Total\n                   -------  ------- --------  -------\nOpening Balance   $400,000  $60,000 $150,000  $610,000\nProfit for year          -        -   90,000    90,000\nNew shares issued   50,000   25,000        -    75,000\nDividends                -        -  (40,000)  (40,000)\n                  -------  ------- --------  --------\nClosing Balance   $450,000  $85,000 $200,000  $735,000"
          },
          {
            id: 4,
            type: "statement-extract",
            marks: 14,
            question: "Prepare the Shareholders' Equity section from Q3 closing balances, including 'Less: Treasury Shares $20,000' if applicable.",
            answer: "SHAREHOLDERS' EQUITY:\n\nShare Capital:\n  Ordinary Shares (450,000 shares @ $1)    $450,000\n\nReserves:\n  Share Premium                             $85,000\n  Retained Earnings                        $200,000\nTotal Reserves                             $285,000\n\nTotal Shareholders' Equity                 $735,000"
          },
          {
            id: 5,
            type: "explanation",
            marks: 10,
            question: "Explain the difference between revenue reserves and capital reserves, giving ONE example of each.",
            answer: "Revenue Reserves:\n- Created from trading profits\n- Can be distributed as dividends\n- Example: Retained Earnings, General Reserve\n\nCapital Reserves:\n- Created from capital transactions\n- Cannot be distributed as dividends\n- Example: Share Premium, Revaluation Surplus\n\nKey difference: Revenue reserves are distributable; capital reserves are not."
          }
        ]
      }
    ]
  },
  strand4: {
    title: "Investment Analysis",
    tests: [
      {
        id: "4.1",
        title: "Investment Ratios - Test",
        duration: "50 minutes",
        totalMarks: 50,
        questions: [
          {
            id: 1,
            type: "calculations",
            marks: 20,
            question: "Calculate all investment ratios:\nNet Profit After Tax: $600,000\nOrdinary Shares: 2,000,000\nMarket Price: $4.00\nDividend per Share: $0.20\nNet Assets: $5,000,000",
            answer: "1. EPS = $600,000 ÷ 2,000,000 = $0.30\n\n2. P/E Ratio = $4.00 ÷ $0.30 = 13.3 times\n\n3. Dividend Yield = ($0.20 ÷ $4.00) × 100 = 5%\n\n4. Dividend Cover = $0.30 ÷ $0.20 = 1.5 times\n\n5. NAV per share = $5,000,000 ÷ 2,000,000 = $2.50"
          },
          {
            id: 2,
            type: "interpretation",
            marks: 12,
            question: "Interpret the P/E ratio and dividend yield from Q1 for an investor.",
            answer: "P/E Ratio (13.3 times):\n- Investors pay $13.30 for each $1 of earnings\n- Moderate growth expectation\n- Compare to industry average to assess if overvalued/undervalued\n\nDividend Yield (5%):\n- Good return for income investors\n- Higher than typical bank deposits (2-3%)\n- Company shares profits with shareholders\n- Attractive for income-focused investors"
          },
          {
            id: 3,
            type: "analysis",
            marks: 8,
            question: "NAV is $2.50 but market price is $4.00. What does this indicate?",
            answer: "Market price exceeds book value by $1.50 per share (60% premium).\n\nThis indicates:\n1. Market values the company's future earnings potential\n2. Intangible assets (brand, goodwill) not on balance sheet\n3. Expected growth justifies premium\n4. May be overvalued if growth doesn't materialize"
          },
          {
            id: 4,
            type: "recommendation",
            marks: 10,
            question: "Based on Q1-3, would you recommend buying these shares? Justify your answer.",
            answer: "Recommendation: MODERATE BUY\n\nPositives:\n- Solid EPS of $0.30\n- Good dividend yield of 5%\n- Dividend cover of 1.5x shows sustainable dividends\n- P/E of 13 is reasonable\n\nCautions:\n- Trading well above NAV\n- Dividend cover could be higher\n\nSuitable for: Balanced investors seeking income (good dividend) and moderate growth. Not for aggressive growth investors."
          }
        ]
      }
    ]
  }
};

// ==================== EXERCISES ====================
export const year11AccountingExercises = {
  strand1: [
    {
      id: "ex-1.1",
      title: "Accounting Equation Practice",
      type: "worksheet",
      questions: [
        "If Assets = $75,000 and Owner's Equity = $45,000, what are the Liabilities?",
        "Complete: Assets $?, Liabilities $30,000, Owner's Equity $50,000",
        "A business has cash $10,000, inventory $15,000, equipment $25,000, and owes $20,000 to suppliers. Calculate Owner's Equity."
      ]
    }
  ],
  strand2: [
    {
      id: "ex-2.1",
      title: "Journal Entry Practice",
      type: "worksheet",
      questions: [
        "Record: Owner invested $20,000 cash to start business",
        "Record: Purchased inventory for $5,000 cash",
        "Record: Sold goods on credit for $3,000",
        "Record: Paid rent $800 by cheque"
      ]
    }
  ]
};

export const year12AccountingExercises = {
  strand2: [
    {
      id: "ex-2.1",
      title: "Inventory Valuation Practice",
      type: "worksheet",
      questions: [
        "Calculate COGS using FIFO for given purchases and sales",
        "Calculate ending inventory using weighted average",
        "Determine inventory value using lower of cost or NRV"
      ]
    }
  ]
};

export const year13AccountingExercises = {
  strand1: [
    {
      id: "ex-1.1",
      title: "Share Capital Practice",
      type: "worksheet",
      questions: [
        "Record share issue at par value",
        "Record share issue at premium",
        "Calculate and record bonus issue"
      ]
    }
  ],
  strand4: [
    {
      id: "ex-4.1",
      title: "Investment Ratio Practice",
      type: "worksheet",
      questions: [
        "Calculate EPS, P/E, dividend yield for given data",
        "Interpret ratios for investment decision",
        "Compare two companies using ratio analysis"
      ]
    }
  ]
};
